/**
 * INSURANCE_WizardLanding_v01 (React + TypeScript + React-Bootstrap)
 * Date: 2025-09-05 (America/Denver)
 *
 * Purpose
 * -------
 * A clean, reusable multi-step wizard landing page that guides a new customer
 * through an insurance application workflow. It uses a single set of UI
 * components that are re-used across steps, basic validation, a review step,
 * and a simulated submit.
 */

import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  ProgressBar,
  Row,
  Spinner,
} from "react-bootstrap";

/* ==============================
   Types
   ============================== */

type AppStep = 0 | 1 | 2 | 3 | 4;

type CoverageOption =
  | "General Liability"
  | "Commercial Auto"
  | "Workers Comp"
  | "Tools & Equipment"
  | "Umbrella";

interface InsuranceAppModel {
  // Step 0: Contact
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  street: string;
  city: string;
  state: string;
  zip: string;

  // Step 1: Business
  businessName: string;
  businessType: string;
  legalStructure: string;
  yearsInBusiness: string;
  numEmployees: string;
  annualRevenue: string;

  // Step 2: Coverage
  coverageNeeds: CoverageOption[];
  priorClaims: string;
  descriptionOfOps: string;

  // Step 3: Notes
  notes: string;
}

/* ==============================
   Helpers
   ============================== */

const required = (v: string) => v.trim().length > 0;
const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const isPhone = (v: string) => /^[0-9\-\+\(\)\s]{7,20}$/.test(v);
const pretty = (v: string | undefined) => (v && v.trim()) || "—";

const allCoverage: CoverageOption[] = [
  "General Liability",
  "Commercial Auto",
  "Workers Comp",
  "Tools & Equipment",
  "Umbrella",
];

const emptyModel: InsuranceAppModel = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  businessName: "",
  businessType: "",
  legalStructure: "",
  yearsInBusiness: "",
  numEmployees: "",
  annualRevenue: "",
  coverageNeeds: [],
  priorClaims: "",
  descriptionOfOps: "",
  notes: "",
};

/* ==============================
   Reusable Field Specs
   ============================== */

type FieldSpec = {
  key: keyof InsuranceAppModel;
  label: string;
  placeholder?: string;
  as?: "input" | "textarea" | "select";
  options?: string[];
  required?: boolean;
};

const CONTACT_FIELDS: FieldSpec[] = [
  { key: "firstName", label: "First Name", required: true },
  { key: "lastName", label: "Last Name", required: true },
  { key: "phone", label: "Phone", required: true },
  { key: "email", label: "Email", required: true },
  { key: "street", label: "Street", required: true },
  { key: "city", label: "City", required: true },
  { key: "state", label: "State", required: true },
  { key: "zip", label: "ZIP", required: true },
];

const BUSINESS_FIELDS: FieldSpec[] = [
  { key: "businessName", label: "Business Name", required: true },
  { key: "businessType", label: "Business Type", required: true },
  {
    key: "legalStructure",
    label: "Legal Structure",
    as: "select",
    options: ["LLC", "S-Corp", "C-Corp", "Sole Proprietor", "Partnership"],
    required: true,
  },
  { key: "yearsInBusiness", label: "Years in Business", required: true },
  { key: "numEmployees", label: "Number of Employees", required: true },
  { key: "annualRevenue", label: "Annual Revenue", required: true },
];

/* ==============================
   Field Renderer
   ============================== */

interface FieldRendererProps {
  model: InsuranceAppModel;
  setModel: React.Dispatch<React.SetStateAction<InsuranceAppModel>>;
  spec: FieldSpec;
  showValidity?: boolean;
}

const FieldRenderer: React.FC<FieldRendererProps> = ({
  model,
  setModel,
  spec,
  showValidity,
}) => {
  const value = model[spec.key] as string;
  const onChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  > = (e) => {
    setModel((m) => ({ ...m, [spec.key]: e.currentTarget.value }));
  };

  const isValid =
    !spec.required ||
    (required(value) &&
      (spec.key !== "email" || isEmail(value)) &&
      (spec.key !== "phone" || isPhone(value)));

  return (
    <Form.Group className="mb-3">
      <Form.Label>{spec.label}</Form.Label>
      {spec.as === "textarea" ? (
        <Form.Control
          as="textarea"
          rows={3}
          value={value}
          onChange={onChange}
          isInvalid={showValidity && !isValid}
        />
      ) : spec.as === "select" ? (
        <Form.Select
          value={value}
          onChange={onChange}
          isInvalid={showValidity && !isValid}
        >
          <option value="">Select...</option>
          {spec.options?.map((o) => (
            <option key={o}>{o}</option>
          ))}
        </Form.Select>
      ) : (
        <Form.Control
          type="text"
          value={value}
          onChange={onChange}
          isInvalid={showValidity && !isValid}
        />
      )}
      {showValidity && !isValid && (
        <Form.Control.Feedback type="invalid">
          Please enter a valid {spec.label}.
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
};

/* ==============================
   Step Components
   ============================== */

const StepContact = (props: any) => (
  <Row className="g-3">
    {CONTACT_FIELDS.map((f) => (
      <Col xs={12} md={6} key={f.key as string}>
        <FieldRenderer {...props} spec={f} />
      </Col>
    ))}
  </Row>
);

const StepBusiness = (props: any) => (
  <Row className="g-3">
    {BUSINESS_FIELDS.map((f) => (
      <Col xs={12} md={6} key={f.key as string}>
        <FieldRenderer {...props} spec={f} />
      </Col>
    ))}
  </Row>
);

const StepCoverage = ({
  model,
  setModel,
}: {
  model: InsuranceAppModel;
  setModel: React.Dispatch<React.SetStateAction<InsuranceAppModel>>;
}) => (
  <Row>
    <Col>
      <Form.Group>
        <Form.Label>Coverage Needed</Form.Label>
        {allCoverage.map((c) => (
          <Form.Check
            key={c}
            type="checkbox"
            label={c}
            checked={model.coverageNeeds.includes(c)}
            onChange={() =>
              setModel((m) => ({
                ...m,
                coverageNeeds: m.coverageNeeds.includes(c)
                  ? m.coverageNeeds.filter((x) => x !== c)
                  : [...m.coverageNeeds, c],
              }))
            }
          />
        ))}
      </Form.Group>
    </Col>
  </Row>
);

const StepNotes = ({
  model,
  setModel,
}: {
  model: InsuranceAppModel;
  setModel: React.Dispatch<React.SetStateAction<InsuranceAppModel>>;
}) => (
  <Form.Group>
    <Form.Label>Additional Notes</Form.Label>
    <Form.Control
      as="textarea"
      rows={4}
      value={model.notes}
      onChange={(e) => setModel((m) => ({ ...m, notes: e.target.value }))}
    />
  </Form.Group>
);

const StepReview = ({ model }: { model: InsuranceAppModel }) => (
  <div>
    <h5>Review Your Application</h5>
    <pre>{JSON.stringify(model, null, 2)}</pre>
  </div>
);

/* ==============================
   Main Component
   ============================== */

export const INSURANCE_WizardLanding_v01: React.FC = () => {
  const [model, setModel] = useState<InsuranceAppModel>(emptyModel);
  const [step, setStep] = useState<AppStep>(0);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState<string | null>(null);

  const totalSteps = 5;

  const next = () => setStep((s) => (s < 4 ? ((s + 1) as AppStep) : s));
  const prev = () => setStep((s) => (s > 0 ? ((s - 1) as AppStep) : s));

  const handleSubmit = async () => {
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1000));
    setSubmitting(false);
    setSubmittedId("APP-" + Math.random().toString(36).slice(2, 8).toUpperCase());
  };

  return (
    <>
      <Navbar bg="light" className="mb-3">
        <Container>
          <Navbar.Brand>Insurance Wizard</Navbar.Brand>
        </Container>
      </Navbar>
      <Container>
        <Card>
          <Card.Header>
            Step {step + 1} of {totalSteps}
            <ProgressBar now={((step + 1) / totalSteps) * 100} />
          </Card.Header>
          <Card.Body>
            <Form>
              {step === 0 && <StepContact model={model} setModel={setModel} />}
              {step === 1 && <StepBusiness model={model} setModel={setModel} />}
              {step === 2 && <StepCoverage model={model} setModel={setModel} />}
              {step === 3 && <StepNotes model={model} setModel={setModel} />}
              {step === 4 && <StepReview model={model} />}
            </Form>
            <div className="d-flex justify-content-between mt-3">
              <Button onClick={prev} disabled={step === 0 || submitting}>
                Previous
              </Button>
              {step < 4 && (
                <Button onClick={next} disabled={submitting}>
                  Next
                </Button>
              )}
              {step === 4 && (
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Spinner size="sm" animation="border" /> Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </Button>
              )}
            </div>
            {submittedId && (
              <Alert variant="success" className="mt-3">
                Application submitted! Confirmation #{submittedId}
              </Alert>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default INSURANCE_WizardLanding_v01;
